import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { B } from '../constants';
import { promos as promosAPI } from '../api';
import { useAuth } from '../AuthContext';
import { promoAppUrl } from '../lib/shareLinks';
import ReelsFeed from '../components/ReelsFeed';
import { planTripPath } from '../lib/planTrip';
import { trackActivity } from '../lib/activityEvents';

export default function PromoScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const trackedPromoRef = useRef(null);
  const [promo, setPromo] = useState(null);
  const [more, setMore] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    promosAPI.get(id)
      .then(data => {
        if (!mounted) return;
        setPromo(data);
        if (trackedPromoRef.current !== id) {
          trackedPromoRef.current = id;
          trackActivity('content', 'promo_opened', { promoId: data?.id ?? id });
        }

        const promoUrl = promoAppUrl(id);
        const thumbnailUrl = data.thumbnailUrl || '';
        const description = data.caption || `Check out "${data.title}" on GreatBreak!`;

        document.title = `${data.title} — GreatBreak`;

        const setMetaTag = (property, content, isName = false) => {
          const selector = isName ? `meta[name="${property}"]` : `meta[property="${property}"]`;
          let meta = document.querySelector(selector);
          if (!meta) {
            meta = document.createElement('meta');
            meta.setAttribute(isName ? 'name' : 'property', property);
            document.head.appendChild(meta);
          }
          meta.setAttribute('content', content);
        };

        setMetaTag('og:title', data.title);
        setMetaTag('og:description', description);
        setMetaTag('og:image', thumbnailUrl);
        setMetaTag('og:url', promoUrl);
        setMetaTag('og:type', 'website');
        setMetaTag('og:site_name', 'GreatBreak');

        setMetaTag('twitter:card', 'summary_large_image', true);
        setMetaTag('twitter:title', data.title, true);
        setMetaTag('twitter:description', description, true);
        setMetaTag('twitter:image', thumbnailUrl, true);
        setMetaTag('twitter:site', '@greatbreak', true);

        setMetaTag('og:image:width', '1200');
        setMetaTag('og:image:height', '630');
        setMetaTag('og:image:alt', data.title);

      })
      .catch(err => {
        if (!mounted) return;
        setError(err.message || 'Promo not found');
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => {
      mounted = false;
      const removeMetaTag = (property, isName = false) => {
        const selector = isName ? `meta[name="${property}"]` : `meta[property="${property}"]`;
        const meta = document.querySelector(selector);
        if (meta) meta.remove();
      };
      const setDefaultMeta = (property, content, isName = false) => {
        const selector = isName ? `meta[name="${property}"]` : `meta[property="${property}"]`;
        let meta = document.querySelector(selector);
        if (!meta) {
          meta = document.createElement('meta');
          meta.setAttribute(isName ? 'name' : 'property', property);
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      };
      removeMetaTag('og:title');
      removeMetaTag('og:description');
      removeMetaTag('og:image');
      removeMetaTag('og:url');
      removeMetaTag('og:type');
      removeMetaTag('og:video');
      removeMetaTag('og:video:secure_url');
      removeMetaTag('og:video:type');
      removeMetaTag('og:video:width');
      removeMetaTag('og:video:height');
      removeMetaTag('og:video:alt');
      removeMetaTag('og:image:width');
      removeMetaTag('og:image:height');
      removeMetaTag('og:image:alt');
      removeMetaTag('twitter:card', true);
      removeMetaTag('twitter:title', true);
      removeMetaTag('twitter:description', true);
      removeMetaTag('twitter:image', true);
      removeMetaTag('twitter:site', true);
      removeMetaTag('twitter:player', true);
      removeMetaTag('twitter:player:width', true);
      removeMetaTag('twitter:player:height', true);
      removeMetaTag('twitter:player:stream', true);
      removeMetaTag('twitter:player:stream:content_type', true);
      setDefaultMeta('og:title', 'GreatBreak - AI Trip Planner');
      setDefaultMeta('og:description', 'Plan your perfect trip with AI. Get personalized day-by-day itineraries with real places, maps, and budget estimates.');
      setDefaultMeta('og:image', 'https://greatbreak.vercel.app/og-image.png');
      setDefaultMeta('og:url', 'https://greatbreak.vercel.app');
      setDefaultMeta('og:type', 'website');
      setDefaultMeta('twitter:card', 'summary_large_image', true);
      setDefaultMeta('twitter:title', 'GreatBreak - AI Trip Planner', true);
      setDefaultMeta('twitter:description', 'Plan your perfect trip with AI.', true);
      setDefaultMeta('twitter:image', 'https://greatbreak.vercel.app/og-image.png', true);
    };
  }, [id]);

  // A shared link is often somebody's first reel: keep the feed scrollable
  // after it instead of dead-ending on a single video.
  useEffect(() => {
    let mounted = true;
    promosAPI.list({ feed: 'ranked' })
      .then(data => {
        if (!mounted) return;
        setMore((data.promos || []).filter(p => String(p.id) !== String(id)));
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, [id]);

  const handleClose = useCallback(() => navigate('/'), [navigate]);

  if (loading) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000' }}>
        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14 }}>Loading...</div>
      </div>
    );
  }

  if (error || !promo) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#000', color: 'white', gap: 16 }}>
        <div style={{ fontSize: 48 }}>😕</div>
        <div style={{ fontSize: 18, fontWeight: 600 }}>{error || 'Promo not found'}</div>
        <button onClick={() => navigate('/')}
          style={{ padding: '12px 24px', borderRadius: 10, border: 'none', background: B.forest, color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
          Go Home
        </button>
      </div>
    );
  }

  // Shared links show anonymous viewers a teaser: never the playable video.
  if (!user) {
    return (
      <div data-testid="promo-teaser" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000', padding: 20 }}>
        <div style={{ width: '100%', maxWidth: 420, textAlign: 'center', color: 'white', fontFamily: "'Montserrat',sans-serif" }}>
          <div style={{ position: 'relative', aspectRatio: '9/16', maxHeight: '60vh', margin: '0 auto 20px', borderRadius: 18, overflow: 'hidden', background: `linear-gradient(135deg,${B.night},#1a3a2a)` }}>
            {promo.thumbnailUrl && (
              <img src={promo.thumbnailUrl} alt={promo.title} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
            )}
            <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', background: 'rgba(0,0,0,0.25)', fontSize: 54 }}>🔒</div>
          </div>
          <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontWeight: 800, marginBottom: 6 }}>{promo.title}</h1>
          {promo.destination && <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', marginBottom: 18 }}>📍 {promo.destination}</p>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/reels')}
              style={{ padding: '12px 22px', borderRadius: 50, border: '1px solid rgba(255,255,255,0.4)', background: 'transparent', color: 'white', cursor: 'pointer', fontSize: 14, fontWeight: 700 }}>
              Open in GreatBreak
            </button>
            <button onClick={() => navigate('/reels', { state: { openAuth: true } })}
              style={{ padding: '12px 22px', borderRadius: 50, border: 'none', background: B.amber, color: B.night, cursor: 'pointer', fontSize: 14, fontWeight: 800 }}>
              Register to watch
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ReelsFeed
      promos={[promo, ...more]}
      startIndex={0}
      onClose={handleClose}
      user={user}
      onDeleted={handleClose}
      onPlanTrip={(p) => navigate(planTripPath(p.destination))}
    />
  );
}
